@use '@/styles/mixins' as *;

.shell {
  @include well;
  overflow: hidden;
  transition:
    border-color $dur-fast,
    background-color $dur-fast;

  &:focus-within {
    border-color: var(--accent);
    background: var(--surface);
  }
}

.bar {
  padding: 0 4px 4px;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 2px;
  padding-top: 4px;
  overflow-x: auto;
  @include no-scrollbar;

  & + & {
    border-top: 1px solid var(--line);
    margin-top: 4px;
  }
}

.tool,
.textTool,
.blankBtn {
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  min-width: 44px;
  height: 44px;
  padding: 0 8px;
  border-radius: 12px;
  font-size: 1.02rem;
  color: var(--text-2);
  -webkit-tap-highlight-color: transparent;
  @include pressable;
  @include focus-ring;

  &:active {
    background: var(--surface-3);
  }

  @include hover {
    background: var(--surface-3);
  }
}

.toolOn {
  background: var(--accent-soft);
  color: var(--accent-text);
}

.textTool {
  font-size: 0.9rem;
  font-weight: 700;
  padding: 0 12px;
}

.blankBtn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 14px;
  margin-right: 4px;
  font-size: 0.92rem;
  font-weight: 700;
  border-radius: 999px;
  background: color-mix(in srgb, var(--cloze) 16%, var(--surface));
  color: var(--cloze);

  @include hover {
    background: color-mix(in srgb, var(--cloze) 24%, var(--surface));
  }
}

.blankBtnOff {
  background: var(--surface-2);
  color: var(--text-2);

  @include hover {
    background: var(--surface-3);
  }
}

// A blank is the hidden word itself, highlighted in the sentence.
.blank {
  display: inline;
  margin: 0 0.05em;
  padding: 0.02em 0.16em 0.05em 0.08em;
  border-radius: 0.35em;
  background: color-mix(in srgb, var(--cloze) 16%, transparent);
  box-shadow: inset 0 -0.12em 0 color-mix(in srgb, var(--cloze) 72%, transparent);
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
  line-height: inherit;

  &:focus-within {
    background: color-mix(in srgb, var(--cloze) 24%, transparent);
    box-shadow: inset 0 -0.14em 0 var(--cloze);
  }

  &[data-empty='true'] {
    padding-right: 1.7em;
  }
}

.badge {
  display: inline-block;
  min-width: 1.05em;
  margin-right: 0.16em;
  padding: 0 0.28em;
  border-radius: 999px;
  background: var(--cloze);
  color: var(--surface);
  font-size: 0.68em;
  font-weight: 800;
  line-height: 1.4;
  text-align: center;
  vertical-align: 0.12em;
  user-select: none;
  cursor: text;
}

.answer {
  display: inline;
  color: var(--cloze);
  font-weight: 700;
  caret-color: var(--text);
}

.hint {
  margin-left: 0.2em;
  color: var(--text-3);
  font-size: 0.78em;
  font-weight: 700;
  user-select: none;
}

.unblank {
  display: none;
  width: 1.15em;
  height: 1.15em;
  margin-left: 0.08em;
  border-radius: 999px;
  color: var(--cloze);
  font-size: 0.95em;
  line-height: 1;
  vertical-align: 0.05em;
  cursor: pointer;

  .blank:focus-within &,
  .blank:hover & {
    display: inline-grid;
    place-items: center;
  }

  &:hover,
  &:focus-visible {
    background: color-mix(in srgb, var(--cloze) 18%, transparent);
  }
}

:global(.ProseMirror-selectednode.node-blank) {
  outline: none;
}

.aa {
  font-family: $font-display;
  font-weight: 600;
}

.mono {
  font-family: $font-mono;
  font-size: 0.85rem;
  font-weight: 700;
}

.swatch {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  box-shadow:
    0 0 0 2px var(--surface),
    0 0 0 3px var(--line);
}

.hl {
  background: var(--mark);
  color: var(--text);
  padding: 0 5px;
  border-radius: 4px;
  font-weight: 700;
}

.content {
  min-height: 108px;
  padding: $space-3 $space-4;
  outline: none;
  line-height: 1.55;
  font-size: max(16px, 1.0625rem);

  :global(p.is-editor-empty:first-child::before) {
    content: attr(data-placeholder);
    float: left;
    height: 0;
    color: var(--text-3);
    pointer-events: none;
  }
}
